/**
 * TalentOS — display vocabulary.
 *
 * Every enum value and dimension key the backend emits is translated to
 * recruiter-facing wording in exactly one place, so the report and the
 * candidate list can never disagree about what a value is called.
 */
import type {
  TalentosGateOutcome,
  TalentosGateStatus,
  TalentosMandatoryItem,
  TalentosRecommendation,
  TalentosSkillsThresholdEvidence,
  TalentosSkillVerdict,
} from './talentosApi'

/** TalentOS's own stored label for a responsibility comparison row, verbatim
 *  -- 'covered' | 'partial' | 'not_evidenced'. Deliberately NOT converted to
 *  a percentage: TalentOS stores only this category per item (see
 *  `evidence_ref.responsibilities[].label` on the `responsibility_alignment`
 *  dimension), never a per-item number, so showing "Aligned X%" would
 *  display a precision the data doesn't actually have. Only the *aggregate*
 *  `responsibility_alignment` dimension score (shown in Section 4's Match
 *  Result table) is a real stored percentage. */
export function responsibilityLabelText(label: string): string {
  switch (label) {
    case 'covered':
      return 'Covered'
    case 'partial':
      return 'Partially covered'
    default:
      return 'Not evidenced'
  }
}

/** The comparison table's "Informational" match cells (Mandatory's "Not
 *  assessed", Seniority's "Informational", Domain's "Scored X%",
 *  Responsibility's "Aligned X%") all render as this same neutral slate
 *  pill in the design, regardless of the underlying value -- colour is
 *  reserved for true pass/fail/partial verdicts (skill match, gate status,
 *  recommendation). Responsibility rows used to be coloured per-label
 *  (green/amber/red); this neutral style matches the design exactly. */
export const NEUTRAL_PILL_CLASSES = 'bg-slate-100 text-slate-600'
export const NEUTRAL_PILL_SYMBOL = '–'

/** The "JD vs Résumé Comparison" table's 4-state verdict, as a short label
 *  and its pill color. Added 2026-09-23 (matching-report field audit). */
const SKILL_VERDICT_LABELS: Record<TalentosSkillVerdict, string> = {
  matched: 'Matched',
  partial: 'Partial',
  not_found: 'Not found',
  insufficient_evidence: 'Insufficient',
}

export function skillVerdictLabel(verdict: TalentosSkillVerdict): string {
  return SKILL_VERDICT_LABELS[verdict]
}

/** Pill fill+text classes for the verdict cell -- solid colour, no ring,
 *  6px radius (handled by the shared `Pill` component), matching the
 *  design's green-100/amber-50/red-100/blue-100 palette exactly. */
export function skillVerdictClasses(verdict: TalentosSkillVerdict): string {
  switch (verdict) {
    case 'matched':
      return 'bg-green-100 text-green-700'
    case 'partial':
      return 'bg-amber-50 text-amber-700'
    case 'not_found':
      return 'bg-red-100 text-red-700'
    case 'insufficient_evidence':
      return 'bg-blue-100 text-blue-700'
  }
}

export function skillVerdictSymbol(verdict: TalentosSkillVerdict): string {
  switch (verdict) {
    case 'matched':
      return '✓'
    case 'partial':
      return '◐'
    case 'not_found':
      return '✕'
    case 'insufficient_evidence':
      return '?'
  }
}

/** "Primary Skill" / "Preferred" — the comparison table's Category column.
 *  Deliberately not just `category` capitalized: 'required' reads as
 *  "Primary Skill" in this specific report, matching the design's own
 *  wording, not a generic capitalization of the DB value. */
export function skillCategoryLabel(category: 'required' | 'preferred'): string {
  return category === 'required' ? 'Primary Skill' : 'Preferred'
}

/**
 * Scored-dimension keys mapped to their report headings. Sourced from
 * talentos/matching/core.py's ADDITIVE_DIMENSIONS plus the three
 * informational readings written at weight 0.
 *
 * An unknown key falls back to a de-slugged version of itself rather than
 * being dropped — a dimension added backend-first should render imperfectly,
 * never vanish from a report a recruiter is relying on.
 */
const DIMENSION_LABELS: Record<string, string> = {
  achievement_impact: 'Impact of achievements',
  relevant_depth: 'Depth in primary skills',
  primary_skills: 'Primary skills',
  domain: 'Domain fit',
  responsibility_alignment: 'Responsibility coverage',
  secondary_skills: 'Secondary skills',
  // informational — weight_applied === 0
  trajectory: 'Trajectory',
  clarity: 'Résumé clarity',
  resume_authenticity: 'Skills backed by work',
}

export function dimensionLabel(key: string): string {
  return (
    DIMENSION_LABELS[key] ??
    key.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase())
  )
}

const RECOMMENDATION_LABELS: Record<TalentosRecommendation, string> = {
  strongly_recommended: 'Strongly recommended',
  recommended: 'Recommended',
  not_recommended: 'Not recommended',
  // Legacy 4-tier values, readable only. They appear when this bundle is live
  // against a database where migration d8f31c6b85a4 has not run -- possible
  // because frontend and backend deploy through separate pipelines.
  //
  // 'strong_hire' gets the SAME wording as strongly_recommended, which is not
  // laziness: the migration is a pure rename for that band (unchanged 75%
  // threshold), so the two values mean the identical thing and showing a
  // recruiter two different phrases for one verdict would invent a
  // distinction. 'consider_with_caution' keeps its own wording because it was
  // a genuinely separate band that the migration dissolves.
  strong_hire: 'Strongly recommended',
  consider_with_caution: 'Consider with caution',
}

/**
 * The headline verdict.
 *
 * A null recommendation is not missing data: the backend enforces
 * (gate_status = 'ineligible') <=> (overall_score IS NULL AND recommendation IS
 * NULL) as a table constraint. So null means the candidate was never scored,
 * and "Not eligible" is the accurate label — never "Unknown", and never a 0%.
 */
export function recommendationLabel(
  recommendation: TalentosRecommendation | null,
  gateStatus: TalentosGateStatus,
): string {
  if (recommendation) return RECOMMENDATION_LABELS[recommendation]
  return gateStatus === 'ineligible' ? 'Not eligible' : 'Not scored'
}

/** Tailwind classes for the verdict pill. Semantic colour only — green and
 *  red carry meaning here, so neither is used decoratively anywhere else.
 *  Palette matches the approved design exactly (green-100/red-100/amber-50
 *  solid fills, no ring) — NOT the emerald/gray-with-ring styling this used
 *  to have. */
export function recommendationPillClasses(
  recommendation: TalentosRecommendation | null,
  gateStatus: TalentosGateStatus,
): string {
  if (!recommendation || gateStatus === 'ineligible') {
    return 'bg-red-100 text-red-700'
  }
  switch (recommendation) {
    case 'strongly_recommended':
      return 'bg-green-100 text-green-700'
    case 'recommended':
      return 'bg-green-100 text-green-700'
    case 'not_recommended':
      return 'bg-red-100 text-red-700'
    // Legacy pre-d8f31c6b85a4 values -- see RECOMMENDATION_LABELS. strong_hire
    // shares strongly_recommended's colour because it is the same band under
    // its old name. consider_with_caution is amber: it was neither a positive
    // nor a negative verdict, and colouring it as either would misreport a
    // band the engine has since dissolved.
    case 'strong_hire':
      return 'bg-green-100 text-green-700'
    case 'consider_with_caution':
      return 'bg-amber-50 text-amber-700'
  }
}

/** Leading symbol for the verdict pill — the design prefixes every status
 *  pill with a glyph (✓/✕/◐), not colour alone. */
export function recommendationPillSymbol(
  recommendation: TalentosRecommendation | null,
  gateStatus: TalentosGateStatus,
): string {
  if (!recommendation || gateStatus === 'ineligible') return '✕'
  switch (recommendation) {
    case 'strongly_recommended':
    case 'recommended':
    case 'strong_hire':
      return '✓'
    case 'not_recommended':
      return '✕'
    case 'consider_with_caution':
      return '◐'
  }
}

/**
 * The experience pill, e.g. "Qualified — 9.2 yrs".
 *
 * `ineligible` is rendered as "Below minimum" rather than a neutral "Not
 * eligible" because that is what the status provably means: classify() in
 * talentos/matching/experience_gate.py only returns `ineligible` on
 * `candidate_years < lower_bound(jd_min)`. Being too senior is a separate
 * `over_qualified` status that still gets scored.
 */
export function experienceLabel(
  gateStatus: TalentosGateStatus,
  years: number | null,
): string {
  const suffix = years === null ? 'years not determined' : `${years.toFixed(1)} yrs`
  switch (gateStatus) {
    case 'ineligible':
      return `Below minimum — ${suffix}`
    case 'over_qualified':
      return `Over-qualified — ${suffix}`
    case 'qualified':
      return `Qualified — ${suffix}`
  }
}

/** Short pill text for the Experience Gate — no years suffix, unlike
 *  `experienceLabel` above (which is written for a sentence, not a pill).
 *  One shared function so every place this gate's status renders (the score
 *  card, the Gate Details section, the comparison table's Total-experience
 *  row) capitalizes it the same way. */
export function experienceStatusLabel(gateStatus: TalentosGateStatus): string {
  switch (gateStatus) {
    case 'ineligible':
      return 'Ineligible'
    case 'over_qualified':
      return 'Over-qualified'
    case 'qualified':
      return 'Qualified'
  }
}

export function experienceStatusSymbol(gateStatus: TalentosGateStatus): string {
  switch (gateStatus) {
    case 'ineligible':
      return '✕'
    case 'over_qualified':
      return '◐'
    case 'qualified':
      return '✓'
  }
}

/** Pill text for the v0.11 gates' raw status -- 'passed'/'failed'/'skipped'
 *  straight off the DB are lowercase, and rendering them as-is prints
 *  "skipped"/"failed" where the design shows "Skipped"/"Failed". `undefined`
 *  means this backend predates the gates entirely (see `hasGateData`). */
export function gateOutcomeLabel(status: TalentosGateOutcome | undefined): string {
  switch (status) {
    case 'passed':
      return 'Passed'
    case 'failed':
      return 'Failed'
    case 'skipped':
      return 'Skipped'
    default:
      return '—'
  }
}

export function gateOutcomeSymbol(status: TalentosGateOutcome | undefined): string {
  switch (status) {
    case 'passed':
      return '✓'
    case 'failed':
      return '✕'
    default:
      return NEUTRAL_PILL_SYMBOL
  }
}

/** Solid fill+text, no ring -- matches the design's palette exactly (green-100
 *  for qualified/passed, amber-50 for the "flagged for review" over-qualified
 *  state, red-100 for ineligible). 'skipped'/unreported gates use the same
 *  neutral slate pill as every other "no finding" cell in the report. */
export function experiencePillClasses(gateStatus: TalentosGateStatus): string {
  switch (gateStatus) {
    case 'ineligible':
      return 'bg-red-100 text-red-700'
    case 'over_qualified':
      return 'bg-amber-50 text-amber-700'
    case 'qualified':
      return 'bg-green-100 text-green-700'
  }
}

/** Same styling family as `experiencePillClasses`, for the two v0.11 gate
 *  pills -- solid fill, no ring, neutral slate for 'skipped'/unreported. */
export function gateOutcomePillClasses(status: TalentosGateOutcome | undefined): string {
  switch (status) {
    case 'passed':
      return 'bg-green-100 text-green-700'
    case 'failed':
      return 'bg-red-100 text-red-700'
    default:
      return NEUTRAL_PILL_CLASSES
  }
}

/** Hex stroke color for the score ring's arc — the same semantic mapping as
 *  recommendationPillClasses, just as a literal color instead of Tailwind
 *  classes (drawn in raw SVG, which can't consume utility classes for a
 *  dynamic stroke attribute). Green-600 (#16a34a), matching the design's own
 *  conic-gradient ring color exactly -- not emerald. */
export function recommendationRingColor(
  recommendation: TalentosRecommendation | null,
  gateStatus: TalentosGateStatus,
): string {
  if (!recommendation || gateStatus === 'ineligible') return '#dc2626'
  switch (recommendation) {
    case 'strongly_recommended':
    case 'strong_hire':
    case 'recommended':
      return '#16a34a'
    case 'not_recommended':
      return '#dc2626'
    case 'consider_with_caution':
      return '#d97706'
  }
}

/** Percent for display. Never invents a number: an unscored run shows an em
 *  dash, because 0% would read as "scored, and scored terribly". Rounded to
 *  a whole number for on-screen text -- `display_percent` itself keeps its
 *  full 2-decimal precision for every other purpose (the score ring's arc
 *  math, weighted-sum arithmetic, etc.); only this text label rounds. */
export function percentText(displayPercent: number | null): string {
  return displayPercent === null ? '—' : `${Math.round(displayPercent)}`
}

/**
 * Mirrors the backend's own conversion for a raw 0-1 score (see
 * talentos/matching/score_composition.py::to_display_percent): nearest whole
 * percent, ties rounding UP (26.22 -> 26, 26.51 -> 27, 26.50 -> 27; product
 * decision 2026-09-23). A host list's fallback score (shown before
 * TalentOS's own summary has loaded) must use this same conversion --
 * formatting the same underlying number two different ways at two different
 * moments makes the displayed percent visibly jump once TalentOS's data
 * arrives, even though nothing about the candidate's score actually changed.
 *
 * `Math.round()` is safe to use directly here, unlike Python's `round()`:
 * JS's Math.round is defined as round-half-AWAY-FROM-ZERO (round-half-up for
 * a non-negative input, which `clamped` always is), not half-to-even, so a
 * .50 always rounds up. No floor(x + 0.5) trick needed on this side.
 */
export function toDisplayPercent(rawScore: number): number {
  const clamped = Math.max(0, Math.min(1, rawScore))
  return Math.round(clamped * 100)
}

// ── v0.11 gates ─────────────────────────────────────────────────────────────
//
// Two rules govern every string below.
//
// 1. State the finding, never the mechanism. A recruiter reads what the
//    candidate does or does not have — not which check ran, in what order, or
//    that one result overrode another.
// 2. Never quote the coverage threshold. It is a constant chosen by a
//    developer; no recruiter, role, or job description set it, so attributing
//    it to "this role" would be a fabrication. `evidence.threshold` exists for
//    traceability and is deliberately not rendered anywhere.

/** Pill text for the essential-requirements check.
 *
 *  Counts come from the evidence list rather than the reason string, which is
 *  the engine's audit sentence and is phrased for a different reader. */
export function mandatoryGateLabel(
  status: TalentosGateOutcome | undefined,
  evidence: TalentosMandatoryItem[] | null | undefined,
): string {
  // undefined status == this backend predates the gates entirely, which is a
  // different thing from every status this function otherwise handles. Falling
  // through would land on the vacuous-pass branch and print 'None listed' --
  // asserting the role listed no essential requirements, a claim no engine
  // made. An em dash states nothing, which is the only honest output here.
  if (status === undefined) return '—'
  if (status === 'skipped') return '—'
  const items = evidence ?? []
  if (status === 'failed') {
    const notMet = items.filter((item) => !item.matched).length
    // A failure with no itemised evidence should still read as a failure, not
    // as "0 missing".
    if (notMet === 0) return 'Not met'
    return notMet === 1 ? '1 missing' : `${notMet} missing`
  }
  // A vacuous pass: this role listed nothing as essential. Saying "all met"
  // would imply a bar was cleared when none was set.
  if (items.length === 0) return 'None listed'
  return items.length === 1 ? 'Met' : `All ${items.length} met`
}

/** Pill text for the required-skills check.
 *
 *  Deliberately a count, not a percentage. "60%" invites "60% of what, and is
 *  that enough?" — a question only the hidden threshold could answer. */
export function skillsCoverageLabel(
  status: TalentosGateOutcome | undefined,
  evidence: TalentosSkillsThresholdEvidence | null | undefined,
): string {
  // As in mandatoryGateLabel: undefined means the backend does not report this
  // gate. Falling through would print 'Not met' -- telling a recruiter the
  // candidate failed a check that never ran. That is the single worst output
  // this file could produce, so it is guarded first and explicitly.
  if (status === undefined) return '—'
  if (status === 'skipped') return '—'
  if (!evidence) return status === 'passed' ? 'None listed' : 'Not met'
  return `${evidence.matched_count} of ${evidence.jd_skill_count} evidenced`
}

/**
 * Whether this backend reports the v0.11 gates at all.
 *
 * The distinction callers need is three-way, not two: a gate can have run
 * ('passed'/'failed'), have been skipped ('skipped'), or be unreported by a
 * backend that predates gates entirely (undefined). Only the third means "do
 * not render a gate section"; 'skipped' is a real finding worth showing.
 *
 * Exists because frontend and backend deploy through separate pipelines, so
 * this bundle can legitimately be newer than the API it is talking to.
 */
export function hasGateData(run: {
  mandatory_gate_status?: TalentosGateOutcome
  skills_threshold_status?: TalentosGateOutcome
}): boolean {
  return run.mandatory_gate_status !== undefined && run.skills_threshold_status !== undefined
}

/**
 * Whether a gate — not the score — decided the verdict.
 *
 * Either gate failing forces `not_recommended` regardless of
 * `display_percent`. When that happens the score must stop presenting itself
 * as the headline: 78% in brand blue beside "Not recommended" reads as a
 * rendering fault. The number is still shown and still accurate; only its
 * frame quietens.
 */
export function verdictDecidedByGate(run: {
  mandatory_gate_status?: TalentosGateOutcome
  skills_threshold_status?: TalentosGateOutcome
}): boolean {
  // Both comparisons are already false when the field is undefined, so an
  // un-upgraded backend yields false -- no gate decided anything, which is
  // correct: it did not run one. The optional types make that explicit rather
  // than accidental.
  return run.mandatory_gate_status === 'failed' || run.skills_threshold_status === 'failed'
}
